import { useState } from 'react'
import { useDispatch } from 'react-redux'

import Container from '@mui/material/Container'
import Typography from '@mui/material/Typography'
import Grid from '@mui/material/Grid'
import TextField from '@mui/material/TextField'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'

import TagSelector from '../tags/TagSelector'
import TerminologySourcesEditor, { emptySource, validateSources } from '../shared/TerminologySourcesEditor'
import TerminologySubmissionThanks from '../shared/TerminologySubmissionThanks'

import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import IconButton from '@mui/material/IconButton'
import CloseIcon from '@mui/icons-material/Close'
import MenuBookIcon from '@mui/icons-material/MenuBook'
import { styled } from '@mui/material/styles'

import { RequestFormStyle } from '../styles'
import { TerminologyModalStyle } from '../styles'
import { setErrorNotification } from '../../reducers/notificationReducer'
import { createTerminology } from '../../reducers/terminologyReducer'
import useResourceSubmit from '../../hooks/useResourceSubmit'
import { validateField } from '../../utils/formValidation'

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
    '& .MuiDialogContent-root': {
        padding: theme.spacing(2),
    },
    '& .MuiDialogActions-root': {
        padding: theme.spacing(1),
    },
}))

const BootstrapDialogTitle = (props) => {
    const { children, onClose, ...other } = props

    return (
        <DialogTitle sx={{ m: 0, p: 2 }} {...other}>
            {children}
            {onClose ? (
                <IconButton
                    aria-label="close"
                    onClick={onClose}
                    sx={{
                        position: 'absolute',
                        right: 8,
                        top: 8,
                        color: (theme) => theme.palette.grey[500],
                    }}
                >
                    <CloseIcon />
                </IconButton>
            ) : null}
        </DialogTitle>
    )
}

const TerminologyForm = ({ user }) => {

    const dispatch = useDispatch()
    const { isSubmitting, submit } = useResourceSubmit()

    const [term, setTerm] = useState('')
    const [termError, setTermError] = useState(false)
    const [termHelper, setTermHelper] = useState('')
    const [description, setDescription] = useState('')
    const [descriptionError, setDescriptionError] = useState(false)
    const [descriptionHelper, setDescriptionHelper] = useState('')
    const [sources, setSources] = useState([emptySource()])
    const [formTags, setFormTags] = useState([])

    const [modalOpen, setModalOpen] = useState(false)

    const reset = () => {
        setTerm('')
        setTermError(false)
        setTermHelper('')
        setDescription('')
        setDescriptionError(false)
        setDescriptionHelper('')
        setSources([emptySource()])
        setFormTags([])
    }

    const handleCloseModal = () => {
        setModalOpen(false)
    }

    const handleSubmit = (event) => {
        event.preventDefault()
        const sourcesCheck = validateSources(sources)

        if (
            validateField(term, setTermError, setTermHelper, 100, 'Term') &&
            validateField(description, setDescriptionError, setDescriptionHelper) &&
            validateField(formTags, undefined, undefined, 5, 'Key Words') &&
            sourcesCheck.ok
        ) {
            const data = {
                term: term,
                description: description,
                tags: (formTags && formTags.length > 0) ? formTags : [],
                sources: sources.map(s => ({
                    source: s.source,
                    url: s.url || null,
                    publication_data: s.publication_data || null,
                    pub_date: s.pub_date || null,
                })),
            }

            // Check if user is staff or terminology admin
            const isAdminOrTerminologyAdmin = user.staff || (user.terminology_admin)
            submit(createTerminology(data, isAdminOrTerminologyAdmin), () => {
                reset()
                setModalOpen(true)
            })
        } else if (!sourcesCheck.ok) {
            dispatch(setErrorNotification(sourcesCheck.message))
        } else {
            dispatch(setErrorNotification('Please fill all required fields before submission'))
        }
    }

    // Check if user is staff or terminology admin for button text
    const isTerminologyAdmin = user.terminology_admin
    return (
        <Container {...RequestFormStyle.container}>
            <Typography {...RequestFormStyle.title.props} gutterBottom sx={RequestFormStyle.title.style}>
                Living Glossary Form
            </Typography>
            <form onSubmit={handleSubmit}>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            value={term}
                            onChange={e => setTerm(e.target.value)}
                            label="Term"
                            required
                            error={termError}
                            helperText={termHelper || `${term.length}/100 characters`}
                            onBlur={()=>validateField(term, setTermError, setTermHelper, 100, 'Term')}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            multiline
                            rows={4}
                            value={description}
                            onChange={e => setDescription(e.target.value)}
                            label="Definition"
                            required
                            error={descriptionError}
                            helperText={descriptionHelper}
                            onBlur={()=>validateField(description, setDescriptionError, setDescriptionHelper)}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TerminologySourcesEditor sources={sources} onChange={setSources} />
                    </Grid>
                    <Grid item xs={12}>
                        <TagSelector
                            tagType="default"
                            selectedTags={formTags}
                            onChange={setFormTags}
                            multiple={true}
                            label="Key Words"
                            required
                            maxTags={5}
                        />
                    </Grid>
                    <Grid item xs={4}></Grid>
                    <Grid item xs={4}>
                        <Button sx={RequestFormStyle.button.style} {...RequestFormStyle.button.props} type='submit' disabled={isSubmitting}>
                            {isTerminologyAdmin ? 'Create Term' : 'Request Term'}
                        </Button>
                    </Grid>
                </Grid>
            </form>

            <BootstrapDialog open={modalOpen} onClose={handleCloseModal} aria-labelledby="submission-dialog-title" fullWidth {...TerminologyModalStyle.modal}>
                <BootstrapDialogTitle id="submission-dialog-title" onClose={handleCloseModal}>
                    <Box sx={TerminologyModalStyle.headingContainer}>
                        <MenuBookIcon sx={TerminologyModalStyle.icon}/>
                        Thank you for your submission
                    </Box>
                </BootstrapDialogTitle>
                <DialogContent dividers>
                    <TerminologySubmissionThanks />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseModal} autoFocus>
                        Ok
                    </Button>
                </DialogActions>
            </BootstrapDialog>
        </Container>
    )
}

export default TerminologyForm
